/**
 * 용량 룰 값 패널 (이 화면의 유일한 신규 핵심 컴포넌트 — 계획 C-8).
 *
 * - raw(적응증별 원본) / broad(판정에 쓰이는 병합값) 토글. 기본은 raw.
 * - `rawContent === null`(전개본/구버전)이면 "근거 원본 없음" 배지 + broad만, 토글 비활성.
 * - 구간 종류 배지·조건 블록·원문 1일량·머리 문장·제외한 금기 표시(spec 2026-09-14 화면 설계).
 * - 세그먼트 인용문 클릭 → onCitationClick(원문)으로 상위(상세 페이지)에 하이라이트를 위임.
 * - 대상 제품 검산 이슈(checks.gate_issues)가 있으면 경고 박스로 표시(D15 — 제품 단위).
 *
 * 새 기준 룰(`criteriaVersion` 있음)만 구간 종류 배지를 단다. 옛 룰은 값으로 종류를 추정하지
 * 않고 "옛 기준" 배지 하나만 단다(D14). "병합됨" 필드 표시도 옛 룰 전용이며, 새 기준 룰은
 * `merged_indications`가 있는 카드에 "효능 합침"으로 대신 표시한다.
 *
 * 순수 프리젠테이션 + 로컬 토글 state만 — 데이터 fetch 없음(ARCHITECTURE §2).
 */
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Button from "components/atoms/Button";
import {
  DosePerUnit,
  DoseRuleContent,
  DoseRuleSegment,
  SegmentKind,
} from "services/api/doseRules/types";
import {
  DOSE_FIELD_LABELS,
  SEGMENT_KIND_LABELS,
  SEGMENT_KIND_TONE,
  SEGMENT_KIND_BODY_PHRASE,
  UNIT_MISSING_WARNING,
  formatDoseValueField,
  formatFieldValue,
  formatLabelDailyDose,
  formatMonthAge,
  getMergedFieldKeys,
  getSegments,
  isDoseValueField,
  patientConditionKindLabel,
  droppedReasonLabel,
  segmentBoundaryText,
  segmentTitle,
} from "utils/doseRule";

export type SegmentPanelMode = "raw" | "broad";

export interface DoseRuleSegmentPanelProps {
  /** 병합본(broad). */
  content: DoseRuleContent;
  /** 적응증별 원본. 전개본/구버전이면 null. */
  rawContent: DoseRuleContent | null;
  /** provenance.criteria_version. 있으면 새 기준(2026-09-14~), 없으면 옛 기준(D13·D14). */
  criteriaVersion?: string | null;
  /** 처방단위(D-1). 용량 값 뒤에 붙인다. targetProduct.dispense→productRef 폴백은 상위에서 해결. */
  prescribeUnit?: string | null;
  /** 1단위 함량(D-1). mg/{처방단위} 꼴이면 성분량 병기. */
  perUnit?: DosePerUnit | null;
  /** 전개본(derived_from 있음)이면 "구버전" 대신 "전개본" 문구를 쓴다(D-4). */
  isDerived?: boolean;
  /**
   * 켜면 "구버전 — 재추출 대상" 배지와 "단위 없음" 경고 줄을 그리지 않는다(기본 false).
   * 판정 기록 상세의 당시 룰 패널만 켠다 — rules_json 폴백·과거 버전 단위에서 거짓 경고가 뜨므로
   * (@spec:2026-09-29-처방검토-판정기록-조회화면 D-12). "옛 기준" 배지는 그 버전의 사실이라 유지한다.
   */
  suppressLegacyBadges?: boolean;
  /** 인용문 클릭 → 상위에 원문 텍스트 전달. */
  onCitationClick?: (text: string) => void;
}

/** 배지 톤 → 색. 금기 빨강·미확립/권장안함 회색·조건부 노랑·효능합침 파랑(화면 설계 표). */
type BadgeTone = "red" | "gray" | "yellow" | "blue";
const BADGE_TONE: Record<BadgeTone, { bg?: string; fg?: string }> = {
  red: { bg: COLOR_SYSTEM.get("Red")?.[50], fg: COLOR_SYSTEM.get("Red")?.[800] },
  gray: { bg: COLOR_SYSTEM.get("Gray")?.[50], fg: COLOR_SYSTEM.get("Gray")?.[600] },
  yellow: { bg: COLOR_SYSTEM.get("Yellow")?.[50], fg: COLOR_SYSTEM.get("Yellow")?.[800] },
  blue: { bg: COLOR_SYSTEM.get("Blue")?.[50], fg: COLOR_SYSTEM.get("Blue")?.[800] },
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const Toggle = styled.div`
  display: inline-flex;
  gap: 6px;
`;

const BadgeRow = styled.div`
  display: inline-flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const LegacyBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${COLOR_SYSTEM.get("Orange")?.[50]};
  color: ${COLOR_SYSTEM.get("Orange")?.[800]};
`;

/** 전개본 안내 배지(D-4) — 구버전과 달리 정상 상태다(파랑 톤). */
const DerivedBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${COLOR_SYSTEM.get("Blue")?.[50]};
  color: ${COLOR_SYSTEM.get("Blue")?.[800]};
`;

/** 처방단위 없음 경고 줄(D-1) — 판정에서 빠지는 룰. */
const UnitWarn = styled.div`
  padding: 8px 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Orange")?.[100]};
  background-color: ${COLOR_SYSTEM.get("Orange")?.[50]};
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Orange")?.[800]};
`;

const KindBadge = styled.span<{ $tone: BadgeTone }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background-color: ${({ $tone }) => BADGE_TONE[$tone].bg ?? "#eee"};
  color: ${({ $tone }) => BADGE_TONE[$tone].fg ?? "#333"};
`;

const ModeNote = styled.div`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const DroppedBox = styled.details`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  background-color: ${COLOR_SYSTEM.get("Gray")?.[10]};
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};

  summary {
    cursor: pointer;
    font-weight: 700;
    color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  }
  ul {
    margin: 8px 0 0;
    padding-left: 18px;
  }
`;

const Card = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 10px;
  padding: 12px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const ConditionBlock = styled.div`
  margin-bottom: 8px;
  padding: 6px 10px;
  border-left: 3px solid ${COLOR_SYSTEM.get("Yellow")?.[400]};
  background-color: ${COLOR_SYSTEM.get("Yellow")?.[10]};
  border-radius: 4px;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const MergedIndications = styled.div`
  margin-bottom: 8px;
  padding: 6px 10px;
  border-left: 3px solid ${COLOR_SYSTEM.get("Blue")?.[300]};
  background-color: ${COLOR_SYSTEM.get("Blue")?.[10]};
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const ContraLine = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Red")?.[700]};
`;

const KindPhrase = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const RangeLine = styled.div`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  margin-top: 4px;
`;

const DailyDoseLine = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 12px;
`;

const Field = styled.div<{ $merged?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 6px;
  background-color: ${({ $merged }) =>
    $merged ? COLOR_SYSTEM.get("Yellow")?.[50] : "transparent"};
`;

const FieldLabel = styled.span`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const FieldValue = styled.span<{ $merged?: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $merged }) =>
    $merged ? COLOR_SYSTEM.get("Yellow")?.[800] : COLOR_SYSTEM.get("Gray")?.[900]};
`;

const MergedTag = styled.span`
  margin-left: 6px;
  font-size: 10px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Yellow")?.[800]};
`;

const CitationBox = styled.div`
  display: block;
  width: 100%;
  text-align: left;
  margin-top: 10px;
  padding: 8px 10px;
  border: 1px dashed ${COLOR_SYSTEM.get("Blue")?.[200]};
  border-radius: 8px;
  background-color: ${COLOR_SYSTEM.get("Blue")?.[10] ?? COLOR_SYSTEM.get("Blue")?.[50]};
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  font-size: 12px;
  line-height: 1.5;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${COLOR_SYSTEM.get("Blue")?.[400]};
    outline-offset: 1px;
  }
`;

const ContextLine = styled.div`
  font-size: 11px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  margin-bottom: 4px;
  font-style: italic;
`;

const CitationMeta = styled.div`
  font-size: 11px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  margin-bottom: 4px;
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  font-size: 13px;
`;

/** 세그먼트에 달 배지 목록(새 기준 룰 전용 — 옛 룰은 빈 배열). */
const segmentBadges = (
  seg: DoseRuleSegment,
  isNewCriteria: boolean,
  isMergedSeg: boolean
): Array<{ label: string; tone: BadgeTone }> => {
  const badges: Array<{ label: string; tone: BadgeTone }> = [];
  if (isNewCriteria) {
    const kind = seg?.segment_kind as SegmentKind | undefined;
    if (kind && kind !== "dose") {
      const tone = SEGMENT_KIND_TONE[kind];
      if (tone) badges.push({ label: SEGMENT_KIND_LABELS[kind], tone });
    }
    if (seg?.patient_condition) badges.push({ label: "조건부", tone: "yellow" });
  }
  if (isMergedSeg) badges.push({ label: "효능 합침", tone: "blue" });
  return badges;
};

const DoseRuleSegmentPanel: React.FC<DoseRuleSegmentPanelProps> = ({
  content,
  rawContent,
  criteriaVersion,
  prescribeUnit,
  perUnit,
  isDerived,
  suppressLegacyBadges = false,
  onCitationClick,
}) => {
  const hasRaw = !!rawContent;
  const isNewCriteria =
    typeof criteriaVersion === "string" && criteriaVersion.trim().length > 0;
  const [mode, setMode] = useState<SegmentPanelMode>(hasRaw ? "raw" : "broad");

  // rawContent 유무가 바뀌면(다른 룰로 전환) 기본 모드 재설정.
  useEffect(() => {
    setMode(hasRaw ? "raw" : "broad");
  }, [hasRaw]);

  // 필드 단위 "병합됨" 표시는 옛 룰 전용. 새 기준 룰은 카드의 merged_indications로 대신한다(D14).
  const mergedFields = useMemo(
    () => (isNewCriteria ? new Set<string>() : getMergedFieldKeys(rawContent)),
    [isNewCriteria, rawContent]
  );

  const activeContent = mode === "broad" ? content : rawContent ?? content;
  const segments = getSegments(activeContent);
  const showFieldMerge = mode === "broad" && !isNewCriteria;

  // 단위 문맥(D-1): 처방단위가 없고 용량 값이 있으면 판정에서 빠지는 룰 경고를 상단에 보인다.
  const hasUnit = typeof prescribeUnit === "string" && prescribeUnit.trim().length > 0;
  const hasDoseValues = segments.some((seg) =>
    Object.keys(DOSE_FIELD_LABELS).some(
      (k) => isDoseValueField(k) && seg?.[k] !== null && seg?.[k] !== undefined && seg?.[k] !== ""
    )
  );
  const showUnitWarning = !hasUnit && hasDoseValues;

  /** 세그먼트 필드 값 표시(D-1): 용량 값엔 단위+성분량, 월령은 세 병기, 그 외는 그대로. */
  const renderFieldValue = (key: string, value: unknown): string => {
    if (isDoseValueField(key) && typeof value === "number") {
      return formatDoseValueField(key, value, { prescribeUnit, perUnit });
    }
    if ((key === "age_min_months" || key === "age_max_months") && typeof value === "number") {
      return formatMonthAge(value);
    }
    return formatFieldValue(value);
  };

  const dropped = Array.isArray(activeContent?.dropped_contraindications)
    ? activeContent.dropped_contraindications
    : [];

  return (
    <Wrap>
      <HeaderRow>
        <Toggle>
          <Button
            buttonType="button"
            type="Text"
            size="Small"
            variant={mode === "raw" ? "Contained" : "Outlined"}
            color={mode === "raw" ? "Primary" : "Secondary"}
            disabled={!hasRaw}
            disabledColor={!hasRaw}
            onClick={() => setMode("raw")}
          >
            원본(적응증별)
          </Button>
          <Button
            buttonType="button"
            type="Text"
            size="Small"
            variant={mode === "broad" ? "Contained" : "Outlined"}
            color={mode === "broad" ? "Primary" : "Secondary"}
            onClick={() => setMode("broad")}
          >
            병합값(판정용)
          </Button>
        </Toggle>
        <BadgeRow>
          {!isNewCriteria ? <LegacyBadge>옛 기준 — 구간 종류 없음</LegacyBadge> : null}
          {!hasRaw && isDerived ? (
            <DerivedBadge>전개본 — 구간 원본은 대표 룰에 있음</DerivedBadge>
          ) : null}
          {!suppressLegacyBadges && !hasRaw && !isDerived ? (
            <LegacyBadge>구버전 — 재추출 대상</LegacyBadge>
          ) : null}
        </BadgeRow>
      </HeaderRow>

      {!suppressLegacyBadges && showUnitWarning ? <UnitWarn>{UNIT_MISSING_WARNING}</UnitWarn> : null}

      <ModeNote>
        {mode === "raw"
          ? "적응증별 원본 세그먼트입니다."
          : isNewCriteria
          ? "판정에 실제 쓰이는 병합값입니다. 효능 합침 구간은 여러 효능을 하나로 합쳤습니다."
          : "판정에 실제 쓰이는 병합값입니다. 노란색 표시는 원본에서 값이 갈려 병합된 필드입니다."}
      </ModeNote>

      {dropped.length > 0 ? (
        <DroppedBox>
          <summary>제외한 금기 {dropped.length}건 — 처방 검토에 쓰지 않음</summary>
          <ul>
            {dropped.map((d, i) => (
              <li key={i}>
                {d?.text}
                {d?.reason ? ` (${droppedReasonLabel(d.reason)})` : ""}
              </li>
            ))}
          </ul>
        </DroppedBox>
      ) : null}

      {segments.length === 0 ? (
        <Empty>표시할 세그먼트가 없습니다.</Empty>
      ) : (
        segments.map((seg, index) => {
          const kind = isNewCriteria
            ? (seg?.segment_kind as SegmentKind | undefined)
            : undefined;
          const isContra = kind === "contraindicated";
          const isNotEstablished = kind === "not_established";
          const isNotRecommended = kind === "not_recommended";
          const condition = seg?.patient_condition ?? null;
          const mergedIndications =
            mode === "broad" &&
            isNewCriteria &&
            Array.isArray(seg?.merged_indications) &&
            seg.merged_indications.length > 0
              ? seg.merged_indications
              : null;
          const isMergedSeg = !!mergedIndications;
          const badges = segmentBadges(seg, isNewCriteria, isMergedSeg);
          const boundaryText = segmentBoundaryText(seg);
          const dailyDose = formatLabelDailyDose(seg?.label_daily_dose);

          // 금기 카드는 6개 용량 필드를 숨기고 "투여 금지"로 대신한다.
          const doseValueKeys = [
            "dosemin",
            "dosemax",
            "daymax",
            "doseminperkg",
            "dosemaxperkg",
            "daymaxperkg",
          ];
          const presentFields = Object.keys(DOSE_FIELD_LABELS).filter((key) => {
            if (isContra && doseValueKeys.includes(key)) return false;
            return seg?.[key] !== null && seg?.[key] !== undefined && seg?.[key] !== "";
          });

          const citationText =
            typeof seg?.citation?.original_text === "string"
              ? seg.citation.original_text
              : "";
          const contextText =
            typeof seg?.citation?.context_text === "string" && seg.citation.context_text.trim()
              ? seg.citation.context_text
              : "";

          return (
            <Card key={index}>
              <CardHead>
                <CardTitle>
                  {isMergedSeg ? "효능 합침 구간" : segmentTitle(seg, index)}
                </CardTitle>
                {badges.map((b, i) => (
                  <KindBadge key={i} $tone={b.tone}>
                    {b.label}
                  </KindBadge>
                ))}
              </CardHead>

              {condition ? (
                <ConditionBlock>
                  조건: {condition.text} ({patientConditionKindLabel(condition.kind)})
                </ConditionBlock>
              ) : null}

              {mergedIndications ? (
                <MergedIndications>{mergedIndications.join(" / ")}</MergedIndications>
              ) : null}

              {isContra ? (
                <>
                  <ContraLine>투여 금지</ContraLine>
                  {boundaryText ? <RangeLine>{boundaryText}</RangeLine> : null}
                </>
              ) : isNotEstablished || isNotRecommended ? (
                <>
                  <KindPhrase>
                    {SEGMENT_KIND_BODY_PHRASE[kind as SegmentKind] ?? SEGMENT_KIND_LABELS[kind as SegmentKind]}
                  </KindPhrase>
                  {boundaryText ? <RangeLine>{boundaryText}</RangeLine> : null}
                </>
              ) : (
                <FieldGrid>
                  {presentFields.length === 0 ? (
                    <FieldLabel>표시할 용량 필드가 없습니다.</FieldLabel>
                  ) : (
                    presentFields.map((key) => {
                      const merged = showFieldMerge && mergedFields.has(key);
                      return (
                        <Field key={key} $merged={merged}>
                          <FieldLabel>{DOSE_FIELD_LABELS[key]}</FieldLabel>
                          <FieldValue $merged={merged}>
                            {renderFieldValue(key, seg[key])}
                            {merged ? <MergedTag>병합됨</MergedTag> : null}
                          </FieldValue>
                        </Field>
                      );
                    })
                  )}
                </FieldGrid>
              )}

              {dailyDose ? <DailyDoseLine>원문 1일량: {dailyDose}</DailyDoseLine> : null}

              {citationText ? (
                <CitationBox
                  role="button"
                  tabIndex={0}
                  onClick={() => onCitationClick?.(citationText)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onCitationClick?.(citationText);
                    }
                  }}
                  title="클릭하면 오른쪽 허가사항 원문에서 해당 문장을 찾습니다."
                >
                  {contextText ? <ContextLine>{contextText}</ContextLine> : null}
                  <CitationMeta>
                    근거 인용
                    {seg.citation?.section ? ` · ${seg.citation.section}` : ""}
                    {seg.citation?.evidence_level
                      ? ` · 근거수준 ${seg.citation.evidence_level}`
                      : ""}
                  </CitationMeta>
                  {citationText}
                </CitationBox>
              ) : null}
            </Card>
          );
        })
      )}
    </Wrap>
  );
};

export default DoseRuleSegmentPanel;
